import { useMutation } from '@tanstack/react-query';
import { PrimaryButtonMT, SecondaryButtonMT } from '@/components/buttons/Buttons';
import { showError, showSuccess } from '@/modules/notification/engine/notification.library';
import { useAuthenticationStore } from '@/store';
import { X4bUserRoleEnum } from '@/constants/roles';
import { BillingApi } from '../../billing.api';
import { BillingRequestDto, REQUEST_TYPE_NAMES, isBillingInfoRequest, isPaymentMethodRequest } from '../../billing.types';
import moment from 'moment';
import styles from './row-expansion-template.module.css';

interface Props {
	data: BillingRequestDto;
	onActionComplete?: () => void;
}

const formatRequestId = (id: number) => {
	return `XB-${id.toString().padStart(10, '0')}`;
};

const formatDate = (dateString?: string) => {
	if (!dateString) return 'N/A';
	return moment(dateString).format('MM/DD/YYYY');
};

const RowExpansionTemplate = ({ data, onActionComplete }: Props) => {
	const hasRoles = useAuthenticationStore((state) => state.hasRoles);
	const canApproveDecline = hasRoles([X4bUserRoleEnum.Manager]);
	const isPending = data.Status === 'Waiting for Approval';

	const approveMutation = useMutation({
		mutationFn: async () => {
			if (isBillingInfoRequest(data)) {
				return await BillingApi.approveBillingInfoRequest(data.Id);
			} else {
				return await BillingApi.approvePaymentMethodRequest(data.Id);
			}
		},
		onSuccess: (response) => {
			if (response.Success) {
				showSuccess('Request approved successfully');
				onActionComplete?.();
			} else {
				showError(response.ErrorMessage || 'Failed to approve request');
			}
		},
		onError: (error) => {
			showError('An error occurred while approving the request');
			console.error('Error approving request:', error);
		},
	});

	const declineMutation = useMutation({
		mutationFn: async () => {
			if (isBillingInfoRequest(data)) {
				return await BillingApi.declineBillingInfoRequest(data.Id);
			} else {
				return await BillingApi.declinePaymentMethodRequest(data.Id);
			}
		},
		onSuccess: (response) => {
			if (response.Success) {
				showSuccess('Request declined successfully');
				onActionComplete?.();
			} else {
				showError(response.ErrorMessage || 'Failed to decline request');
			}
		},
		onError: (error) => {
			showError('An error occurred while declining the request');
			console.error('Error declining request:', error);
		},
	});

	return (
		<div className={styles.collapsibleContent}>
			<div className={styles.requestContent}>
				<div className="flex items-center gap-6 tablet:flex-col tablet:items-stretch">
					<div className="flex-1">
						<div>
							<div className={styles.contentLabel}>Request Type</div>
							<div className={styles.emphasis}>
								{REQUEST_TYPE_NAMES[data.RequestType]}
							</div>
						</div>

						<div className="grid grid-cols-4 gap-4 mt-6 tablet:grid-cols-2 tablet:gap-x-6 tablet:gap-y-4">
							<div>
								<div className={styles.contentLabel}>Request ID</div>
								<div>{formatRequestId(data.Id)}</div>
							</div>
							<div>
								<div className={styles.contentLabel}>
									Date Requested
								</div>
								<div>{formatDate(data.DateRequested)}</div>
							</div>
							<div>
								<div className={styles.contentLabel}>Request Type</div>
								<div>{formatDate(data.DateApprovedDeclined)}</div>
							</div>
							<div>
								<div className={styles.contentLabel}>Requester</div>
								<div>{data.UserFullName || 'N/A'}</div>
							</div>
						</div>

						{/* Request-specific details */}
						{isBillingInfoRequest(data) && (
							<div className="grid grid-cols-2 gap-4 mt-6 tablet:gap-x-6 tablet:gap-y-4">
								<div>
									<div className={styles.contentLabel}>Legal Name</div>
									<div>{data.LegalName || 'N/A'}</div>
								</div>
								<div>
									<div className={styles.contentLabel}>Address</div>
									<div>
										{data.Address1}
										{data.Address2 && `, ${data.Address2}`}
									</div>
								</div>
								<div>
									<div className={styles.contentLabel}>City / Province</div>
									<div>{data.CityName && data.ProvinceName ? `${data.CityName}, ${data.ProvinceName}` : 'N/A'}</div>
								</div>
								<div>
									<div className={styles.contentLabel}>Postal Code</div>
									<div>{data.PostalCode || 'N/A'}</div>
								</div>
								<div>
									<div className={styles.contentLabel}>TIN</div>
									<div>{data.Tin || 'N/A'}</div>
								</div>
							</div>
						)}

						{isPaymentMethodRequest(data) && (
							<div className="grid grid-cols-2 gap-4 mt-6 tablet:gap-x-6 tablet:gap-y-4">
								<div>
									<div className={styles.contentLabel}>Old Card</div>
									<div>{data.OldCardType ? `${data.OldCardType} •••• ${data.OldCardLast4}` : 'N/A'}</div>
								</div>
								<div>
									<div className={styles.contentLabel}>New Card</div>
									<div>{data.CardType ? `${data.CardType} •••• ${data.Last4}` : 'N/A'}</div>
								</div>
								<div>
									<div className={styles.contentLabel}>Expiration</div>
									<div>{data.ExpMonth && data.ExpYear ? `${String(data.ExpMonth).padStart(2, '0')}/${String(data.ExpYear).slice(-2)}` : 'N/A'}</div>
								</div>
							</div>
						)}
					</div>

					{isPending && canApproveDecline && (
						<div className="flex items-center gap-4 ml-auto tablet:w-full tablet:justify-end tablet:ml-0">
							<PrimaryButtonMT
								id="approve"
								label="Approve"
								onClick={() => approveMutation.mutate()}
								disabled={approveMutation.isPending || declineMutation.isPending}
								loading={approveMutation.isPending}
							/>
							<SecondaryButtonMT
								id="decline"
								label="Decline"
								onClick={() => declineMutation.mutate()}
								disabled={approveMutation.isPending || declineMutation.isPending}
								loading={declineMutation.isPending}
							/>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default RowExpansionTemplate;
